/**
 * Work around NativeWind's css-interop dropping function-valued `style` props
 * on native.
 *
 * The nativewind jsxImportSource routes every core RN component through
 * react-native-css-interop, which rebuilds the `style` prop from parsed
 * rules. A function-valued style — the standard Pressable
 * `style={({ pressed }) => ...}` pattern — has no enumerable style
 * properties, so the interop's computed style replaces it with nothing and
 * the Pressable renders completely unstyled on Android/iOS: lost padding,
 * borders, radii, and flex direction across the app (misplaced chevrons,
 * crammed settings rows, square primary buttons). Web is unaffected because
 * react-native-web resolves function styles itself.
 *
 * Until nativewind supports function styles, intercept the interop-component
 * registry lookup the wrapped jsx runtime performs for every element and
 * swap Pressable's entry for a shim that resolves the style function to a
 * plain style (tracking the pressed state itself) before handing off to the
 * interop component. The interop applies static styles correctly, and
 * keeping the rendered component type stable avoids remounting subtrees
 * when a style prop switches between function and object forms. Patching
 * `get` (instead of eagerly re-registering the entry) keeps module execution
 * order untouched — the registration module only runs at first render — and
 * survives however late that registration happens. This module must be
 * imported for its side effect from the app entry.
 */
import {
  createElement,
  forwardRef,
  useCallback,
  useState,
  type ComponentType,
  type Ref,
} from "react";
import {
  Platform,
  Pressable,
  type GestureResponderEvent,
  type PressableProps,
  type View,
} from "react-native";
import { interopComponents } from "react-native-css-interop/dist/runtime/api";

type InteropPressableProps = PressableProps & {
  className?: string;
  ref?: Ref<View>;
};

if (Platform.OS !== "web") {
  let shim: ComponentType<InteropPressableProps> | undefined;
  const registryGet = interopComponents.get.bind(interopComponents);

  const makeShim = (interopPressable: ComponentType<InteropPressableProps>) =>
    forwardRef<View, InteropPressableProps>(
      function FunctionStyleSafePressable(props, ref) {
        const [pressed, setPressed] = useState(false);
        const { style, onPressIn, onPressOut } = props;
        const isFunctionStyle = typeof style === "function";

        const handlePressIn = useCallback(
          (event: GestureResponderEvent) => {
            setPressed(true);
            onPressIn?.(event);
          },
          [onPressIn],
        );
        const handlePressOut = useCallback(
          (event: GestureResponderEvent) => {
            setPressed(false);
            onPressOut?.(event);
          },
          [onPressOut],
        );

        if (!isFunctionStyle) {
          return createElement(interopPressable, { ...props, ref });
        }
        return createElement(interopPressable, {
          ...props,
          style: style({ pressed }),
          onPressIn: handlePressIn,
          onPressOut: handlePressOut,
          ref,
        });
      },
    );

  interopComponents.get = (key) => {
    const entry = registryGet(key);
    if (key === Pressable && entry) {
      // The registry stores ComponentType<{}>; Pressable's entry accepts
      // Pressable props plus className.
      shim ??= makeShim(entry as ComponentType<InteropPressableProps>);
      return shim as ComponentType<Record<string, never>>;
    }
    return entry;
  };
}

export {};
